import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api as httpApi } from 'lib/httpClient';

import { useNotification } from 'hooks/ui/useNotification';
import { useEffect } from 'react';
import { ApiResponse, SuccessResponse } from 'further-types/api';
import { Api } from 'further-types/investment';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';
import trpcApi from 'lib/trpcClient';

const useInvestmentFetchAPI = (investmentId: string) =>
  useQuery<Api.GetInvestmentDetailsResponse>({
    queryKey: ['investment', investmentId],
    queryFn: async () => {
      const {
        data: { data },
      } = await httpApi.get(`investment/${investmentId}/details`);
      return data;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: !!investmentId,
  });

export const useInvestmentUpdateAPI = (
  investmentId: string,
  onNegativeBalanceError?: () => void,
) => {
  const notification = useNotification();
  const queryClient = useQueryClient();
  const update = useMutation<
    ApiResponse<SuccessResponse>,
    Error,
    Api.UpdateInvestmentRequest
  >({
    mutationFn: async (data) => {
      const { data: response } = await httpApi.put(
        `investment/${investmentId}`,
        data,
      );
      return response;
    },
    onSettled: async () => {
      queryClient.invalidateQueries({
        queryKey: ['investment', investmentId],
      });
      queryClient.invalidateQueries({ queryKey: ['payments', investmentId] });
      queryClient.invalidateQueries({
        queryKey: ['investment-fees', investmentId],
      });
    },
  });

  useEffect(
    function onSuccess() {
      const successMessage = update.isSuccess && update.data?.responseMsg;
      if (successMessage) {
        notification.success(successMessage);
      }
    },
    [update.isSuccess, update.data],
  );

  useEffect(
    function onError() {
      // @ts-expect-error
      const errorMessage = update.error?.response?.data?.responseMsg;

      const isNegativeBalanceError = errorMessage === 'Negative balance error';

      if (errorMessage?.length && !isNegativeBalanceError) {
        notification.error(errorMessage);
      }

      if (isNegativeBalanceError) {
        if (onNegativeBalanceError) {
          onNegativeBalanceError();
        } else {
          notification.error(errorMessage);
        }
      }
    },
    [update.error],
  );

  return update;
};

const useInvestmentCanAffordNewFees = (params: {
  investmentId: string;
  coreFeeTemplateId?: string;
  investmentAmount?: number;
  disableAffordNewFeesCheck?: boolean;
}) => {
  const {
    investmentId,
    coreFeeTemplateId,
    investmentAmount,
    disableAffordNewFeesCheck,
  } = params;

  return useQuery({
    queryKey: [
      'investment-can-afford-fees',
      investmentId,
      coreFeeTemplateId,
      investmentAmount,
    ],
    queryFn: async () => {
      const {
        data: { data },
      } = await httpApi.get<ApiResponse<SuccessResponse>>(
        `investment/${investmentId}/can-afford-new-fees`,
        {
          params: {
            coreFeeTemplateId,
            investmentAmount,
          },
        },
      );
      return data.success;
    },
    enabled: !!investmentId && !!investmentAmount && !disableAffordNewFeesCheck,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

export const useSendPaymentReminder = () => {
  const notification = useNotification();
  const sendPaymentReminder = useMutation({
    mutationFn: async (investmentId) => {
      try {
        const { data: response } = await httpApi.post(
          `investment/${investmentId}/send-payment-reminder`,
        );
        notification.success(response.responseMsg);
        return response;
      } catch (error: any) {
        notification.error(error?.response?.data?.responseMsg ?? '');
        throw error;
      }
    },
  });
  return {
    sendPaymentReminder,
  };
};

export const useInvestments = ({
  params,
}: {
  params: Api.ListInvestmentsRequestQueryParams;
}) =>
  useQuery<Api.ListInvestmentsResponse>({
    queryKey: ['investments', { params }],
    queryFn: async () => {
      const {
        data: { data },
      } = await httpApi.get<ApiResponse<Api.ListInvestmentsResponse>>(
        `investment`,
        { params },
      );
      return data;
    },
    placeholderData: keepPreviousData,
    enabled: !params.includeExportData,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

export const useCreateInvestment = ({
  onCreate,
  onError,
}: {
  onCreate: (id: string) => void;
  onError?: () => void;
}) => {
  const utils = trpcApi.useUtils();
  const notification = useNotification();
  const queryClient = useQueryClient();
  const handleApiException = useApiExceptionHandler();

  return useMutation({
    mutationFn: async (dto: Api.CreateInvestmentRequest) => {
      const { data: response } = await httpApi.post<
        ApiResponse<Api.CreateInvestmentResponse>
      >(`investment/`, dto);
      return response;
    },
    onSuccess: (data) => {
      notification.success('Investment has been created.');
      queryClient.invalidateQueries({
        queryKey: ['investment'],
      });
      // invalidate investor queries because disableAllEmails might have changed
      utils.investor.getInvestor.invalidate();
      utils.investor.listInvestors.invalidate();
      onCreate?.(`${data?.data?._id}`);
    },
    onError: (error: Error) => {
      handleApiException(error);
      onError?.();
    },
  });
};

export function useInvestment({
  investmentId,
  onNegativeBalanceError,
  coreFeeTemplateId,
  investmentAmount,
  disableAffordNewFeesCheck,
}: {
  investmentId: string;
  onNegativeBalanceError?: () => void;
  coreFeeTemplateId?: string;
  investmentAmount?: number;
  adviserFee?: number;
  disableAffordNewFeesCheck?: boolean;
}) {
  const fetch = useInvestmentFetchAPI(investmentId);
  const update = useInvestmentUpdateAPI(investmentId, onNegativeBalanceError);
  const canAffordNewFees = useInvestmentCanAffordNewFees({
    investmentId,
    coreFeeTemplateId,
    investmentAmount,
    disableAffordNewFeesCheck,
  });
  return {
    fetch,
    update,
    canAffordNewFees,
  };
}

export function useInvestmentsExport({
  params,
}: {
  params: Api.BaseListInvestmentsRequestQueryParams;
}) {
  return useQuery({
    queryKey: ['investments', { params }],
    queryFn: async () => {
      const {
        data: { data },
      } = await httpApi.get(`investment`, { params });
      return data;
    },
    placeholderData: undefined,
    enabled: params.includeExportData,
  });
}
